import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  cover_image_url: string | null;
  pillar: string | null;
  seo_title: string | null;
  seo_description: string | null;
  status: string;
  published_at: string | null;
  reading_time: number | null;
  created_at: string | null;
  updated_at: string | null;
}

// As tres consultas ficam aqui como funcoes puras porque rodam em DOIS lugares:
// nos hooks abaixo (no navegador) e nos `loader` das rotas em src/App.tsx, que
// o vite-react-ssg executa em tempo de build para que o HTML estatico ja saia
// com os posts dentro. Duplicar o select nos dois lados era o caminho certo
// para os dois divergirem.

export const buscarPosts = async (pillarFilter?: string | null) => {
  let query = supabase
    .from('blog_posts')
    .select('id, title, slug, excerpt, cover_image_url, pillar, published_at, reading_time')
    .eq('status', 'published')
    .order('published_at', { ascending: false });

  if (pillarFilter) {
    query = query.eq('pillar', pillarFilter);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as BlogPost[];
};

export const buscarPost = async (slug: string) => {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle();

  if (error) throw error;
  return data as BlogPost | null;
};

export const buscarDestaques = async () => {
  const { data, error } = await supabase
    .from('blog_posts')
    .select('id, title, slug, excerpt, pillar, published_at, reading_time, cover_image_url')
    .eq('status', 'published')
    .order('published_at', { ascending: false })
    .limit(3);

  if (error) throw error;
  return data as BlogPost[];
};

// `dadosIniciais` vem do loader da rota. Durante a geracao estatica ele ja esta
// preenchido, entao o useQuery devolve os dados no primeiro render em vez de
// `isLoading` -- que era o que fazia o HTML do blog sair vazio.

export const useBlogPosts = (pillarFilter?: string | null, dadosIniciais?: BlogPost[]) => {
  return useQuery({
    queryKey: ['blog-posts', pillarFilter],
    queryFn: () => buscarPosts(pillarFilter),
    // So serve para a lista sem filtro: o loader nao conhece o pilar escolhido.
    initialData: pillarFilter ? undefined : dadosIniciais,
  });
};

export const useBlogPost = (slug: string, dadosIniciais?: BlogPost | null) => {
  return useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => buscarPost(slug),
    enabled: !!slug,
    initialData: dadosIniciais ?? undefined,
  });
};

export const useBlogDestaques = (dadosIniciais?: BlogPost[]) => {
  return useQuery({
    queryKey: ['blog-highlights'],
    queryFn: buscarDestaques,
    initialData: dadosIniciais,
  });
};
